import { z } from 'zod';
import type { ItemType } from './types';

// Note payload schema
export const notePayloadSchema = z.object({
  content: z.string().default(''),
});

// Password payload schema
export const passwordPayloadSchema = z.object({
  username: z.string().default(''),
  password: z.string().default(''),
  url: z.string().default(''),
  notes: z.string().default(''),
});

// Card payload schema
export const cardPayloadSchema = z.object({
  cardholderName: z.string().default(''),
  cardNumber: z.string().default(''),
  expiryMonth: z.string().default(''),
  expiryYear: z.string().default(''),
  cvv: z.string().default(''),
  notes: z.string().default(''),
});

// ID payload schema
export const idPayloadSchema = z.object({
  type: z.string().default(''), // e.g., 'Driver\'s License', 'Passport'
  country: z.string().default(''),
  number: z.string().default(''),
  expiryDate: z.string().default(''), // YYYY-MM-DD
  notes: z.string().default(''),
});

// Software Key payload schema
export const softwareKeyPayloadSchema = z.object({
  licenseKey: z.string().default(''),
  email: z.string().default(''),
  version: z.string().default(''),
  url: z.string().default(''),
  notes: z.string().default(''),
});

// File / Photo payload schema (mostly metadata, but allows description/notes)
export const filePayloadSchema = z.object({
  notes: z.string().default(''),
});

export const photoPayloadSchema = z.object({
  notes: z.string().default(''),
  width: z.number().optional(),
  height: z.number().optional(),
});

// Helper to get the schema for a given item type
export function getPayloadSchema(type: ItemType) {
  switch (type) {
    case 'note':
      return notePayloadSchema;
    case 'password':
      return passwordPayloadSchema;
    case 'card':
      return cardPayloadSchema;
    case 'id':
      return idPayloadSchema;
    case 'software_key':
      return softwareKeyPayloadSchema;
    case 'file':
      return filePayloadSchema;
    case 'photo':
      return photoPayloadSchema;
    default:
      return z.record(z.string(), z.unknown());
  }
}

// Full Item creation validation schema (metadata + payload)
export const createItemSchema = z.object({
  title: z.string().min(1, 'Title is required').max(100, 'Title is too long'),
  item_type: z.enum(['file', 'photo', 'note', 'password', 'card', 'id', 'software_key']),
  favorite: z.boolean().default(false),
  payload: z.record(z.string(), z.unknown()).default({}),
  storage_ref: z.string().nullable().default(null),
  storage_meta: z.object({
    size: z.number().optional(),
    mime: z.string().optional(),
    original_name: z.string().optional(),
  }).nullable().default(null),
});

export type NotePayload = z.infer<typeof notePayloadSchema>;
export type PasswordPayload = z.infer<typeof passwordPayloadSchema>;
export type CardPayload = z.infer<typeof cardPayloadSchema>;
export type IdPayload = z.infer<typeof idPayloadSchema>;
export type SoftwareKeyPayload = z.infer<typeof softwareKeyPayloadSchema>;
export type FilePayload = z.infer<typeof filePayloadSchema>;
export type PhotoPayload = z.infer<typeof photoPayloadSchema>;
